import { ListFilter } from 'lucide-react';

import {
    type AppliedFilters,
    type FilterGroup,
    type FilterOption,
    rangeEnds,
    rangeValue,
} from '@/components/FilterBar/filters';
import type { NamedRow, OrderLookups } from '@/features/orders/api';
import { ORDER_STATUS, PAYMENT_STATUS } from '@/features/orders/orders';
import { type OrderQueueParams } from '@/features/reports/api';
import { TRANSACTIONS_COPY } from '@/features/reports/copy';
import { QUEUE_ORDER_STATUSES, QUEUE_PAYMENT_STATUSES } from '@/features/reports/tabs';

/*
  The order queue's drawer (ALS-86): the prototype's "subset worth keeping from the legacy
  report", as Franka listed it on 21 Oct 2026. The values are the Orders register's own (its
  lookups, its status and payment names), so a filter means here what it means there.
*/

export const QUEUE_FILTER_KEYS = {
    status: 'status',
    payment: 'payment',
    rm: 'rm',
    brand: 'brand',
    category: 'category',
    supplier: 'supplier',
    dates: 'dates',
} as const;

export type QueueFilterKey = (typeof QUEUE_FILTER_KEYS)[keyof typeof QUEUE_FILTER_KEYS];

const ANY = 'All';
const L = TRANSACTIONS_COPY.orders.filterLabels;

const named = (rows: readonly NamedRow[]): FilterOption[] => [
    { value: ANY, label: L.any },
    ...rows.map((row) => ({ value: String(row.id), label: row.name })),
];

export function queueFilterGroups(lookups: OrderLookups): FilterGroup<QueueFilterKey>[] {
    const K = QUEUE_FILTER_KEYS;
    return [
        {
            key: 'queue',
            icon: ListFilter,
            title: TRANSACTIONS_COPY.orders.drawer.group,
            subtitle: TRANSACTIONS_COPY.orders.drawer.subtitle,
            fields: [
                {
                    key: K.status,
                    kind: 'choice',
                    label: L.status,
                    chipLabel: L.status,
                    neutral: ANY,
                    // A cancelled or deleted order is never in the queue, so neither is offered.
                    options: [
                        { value: ANY, label: L.any },
                        { value: 'InProgress', label: ORDER_STATUS.InProgress.label },
                        { value: 'Completed', label: ORDER_STATUS.Completed.label },
                    ],
                },
                {
                    key: K.payment,
                    kind: 'choice',
                    label: L.payment,
                    chipLabel: L.payment,
                    neutral: ANY,
                    options: [
                        { value: ANY, label: L.any },
                        ...Object.entries(PAYMENT_STATUS).map(([value, { label }]) => ({
                            value,
                            label,
                        })),
                    ],
                },
                {
                    key: K.rm,
                    kind: 'choice',
                    label: L.rm,
                    chipLabel: 'RM',
                    neutral: ANY,
                    options: named(lookups.relationshipManagers),
                },
                {
                    key: K.brand,
                    kind: 'choice',
                    label: L.brand,
                    chipLabel: L.brand,
                    neutral: ANY,
                    searchable: true,
                    options: named(lookups.brands),
                },
                {
                    key: K.category,
                    kind: 'choice',
                    label: L.category,
                    chipLabel: L.category,
                    neutral: ANY,
                    options: named(lookups.categories),
                },
                {
                    key: K.supplier,
                    kind: 'choice',
                    label: L.supplier,
                    chipLabel: L.supplier,
                    neutral: ANY,
                    searchable: true,
                    options: named(lookups.suppliers),
                },
                { key: K.dates, kind: 'daterange', label: L.dates, chipLabel: L.dates },
            ],
        },
    ];
}

/** What the URL holds, as the drawer edits it. */
export function appliedFromQueue(params: OrderQueueParams): AppliedFilters<QueueFilterKey> {
    const applied: AppliedFilters<QueueFilterKey> = {};
    if (params.status) applied.status = params.status;
    if (params.payment) applied.payment = params.payment;
    if (params.rmId) applied.rm = String(params.rmId);
    if (params.brandId) applied.brand = String(params.brandId);
    if (params.categoryId) applied.category = String(params.categoryId);
    if (params.supplierId) applied.supplier = String(params.supplierId);
    if (params.from || params.to) applied.dates = rangeValue(params.from, params.to);
    return applied;
}

/** The drawer's set back into the queue's params, keeping the search and the bucket it does not own. */
export function queueFromApplied(
    applied: AppliedFilters<QueueFilterKey>,
    keep: Pick<OrderQueueParams, 'q' | 'bucket'>,
): OrderQueueParams {
    const choice = (key: QueueFilterKey) => {
        const value = applied[key];
        return typeof value === 'string' && value !== ANY && value !== '' ? value : undefined;
    };
    const id = (key: QueueFilterKey) => {
        const value = choice(key);
        return value === undefined ? undefined : Number(value);
    };
    const { from, to } = rangeEnds(typeof applied.dates === 'string' ? applied.dates : undefined);
    return {
        ...keep,
        status: oneOf(QUEUE_ORDER_STATUSES, choice('status')),
        payment: oneOf(QUEUE_PAYMENT_STATUSES, choice('payment')),
        rmId: id('rm'),
        brandId: id('brand'),
        categoryId: id('category'),
        supplierId: id('supplier'),
        from: from || undefined,
        to: to || undefined,
    };
}

/** The value when it is one of `allowed`, else not applied. */
function oneOf<T extends string>(allowed: readonly T[], value: string | undefined): T | undefined {
    return allowed.find((one) => one === value);
}

/** How many of the drawer's filters are on, for the Filters button. */
export function queueFilterCount(params: OrderQueueParams): number {
    return Object.keys(appliedFromQueue(params)).length;
}
